/**
 * LaTeX math (KaTeX) for agent markdown.
 *
 * Streamdown takes math via `plugins` (a channel separate from
 * `remarkPlugins`); passed there, it appends a remark parser + rehype-katex to
 * the pipeline and keeps KaTeX's output past Streamdown's sanitizer, which a
 * hand-rolled rehype-katex would be stripped by. KaTeX's CSS is imported
 * beside `<AgentMarkdown>`.
 *
 * Three delimiter families reach KaTeX, which is every form agent output
 * actually uses: `$$…$$`, the TeX/MathJax brackets `\(…\)` (inline) and
 * `\[…\]` (block), and inline `$…$`.
 *
 * Single dollars are the delicate one, because `$` doubles as currency and
 * agents in this product write a lot of it. remark-math's own
 * `singleDollarTextMath` has no currency heuristic — the maintainers closed
 * that as wontfix — so it stays off and `chat/dollar-math.ts` supplies a
 * single-dollar construct carrying markdown-it-katex's delimiter test instead.
 * That is the same rule the Tiptap editor already runs
 * (`forest/markdown-editor/math.ts`), so a doc and a chat message render the
 * same source identically.
 */

import { createMathPlugin, type MathPlugin } from "@streamdown/math";
import { mathFromMarkdown, mathToMarkdown } from "mdast-util-math";
import { math } from "micromark-extension-llm-math";
// Side-effect type imports: these declare the `micromarkExtensions` /
// `fromMarkdownExtensions` / `toMarkdownExtensions` fields on unified's `Data`
// that the plugin below pushes onto. remark-math gets them from triple-slash
// references, which oxfmt hoists imports above and thereby disables — a
// type-only import is the formatter-stable equivalent.
import type {} from "remark-parse";
import type {} from "remark-stringify";
import type { Processor } from "unified";

import { dollarMath } from "./dollar-math";

/**
 * `remark-math` over `micromark-extension-llm-math`'s tokenizer, plus our
 * single-dollar construct.
 *
 * Stock remark-math only knows dollars, so bracket math rendered as literal
 * backslashes. `micromark-extension-llm-math` is a fork of
 * `micromark-extension-math` that adds the bracket delimiters; everything else
 * about the syntax, and both mdast utilities, is unchanged. This is
 * remark-math's own body verbatim — the plugin is nothing but three `data()`
 * pushes — with `math()` swapped for the fork's and `dollarMath` appended.
 *
 * Swapping here rather than aliasing `micromark-extension-math` package-wide
 * keeps it to the one pipeline that wants it: the Tiptap editor's math
 * (`forest/markdown-editor/math.ts`) is a different parser and unaffected.
 *
 * The fork is pinned to an exact version: its `3.1.1-20250610` is a
 * prerelease-form string, and `^` ranges match prereleases unintuitively.
 *
 * There is no `singleDollarTextMath` knob: `dollarMath` is what reads single
 * dollars here, and the fork's eager version must always stay off. The
 * serializer is told single dollars *are* math so an `inlineMath` node round
 * trips as `$x$` rather than being widened to `$$x$$`.
 */
export function remarkLlmMath(this: Processor): undefined {
  const data = this.data();

  const micromarkExtensions = (data.micromarkExtensions ??= []);
  const fromMarkdownExtensions = (data.fromMarkdownExtensions ??= []);
  const toMarkdownExtensions = (data.toMarkdownExtensions ??= []);

  micromarkExtensions.push(math({ singleDollarTextMath: false }), dollarMath);
  fromMarkdownExtensions.push(mathFromMarkdown());
  toMarkdownExtensions.push(mathToMarkdown({ singleDollarTextMath: true }));
}

// `createMathPlugin` is a plain object factory (`{remarkPlugin, rehypePlugin,
// getStyles}`), so spreading it and replacing only `remarkPlugin` swaps the
// parser while keeping rehype-katex and the `katex` plugin name that
// Streamdown's sanitizer allows through.
export const MATH_PLUGIN: MathPlugin = {
  ...createMathPlugin(),
  remarkPlugin: remarkLlmMath,
};
